import { Container, Sprite, Texture } from 'pixi.js';
import type { Game } from './Game';
import { loadFrames } from './objects/ObjectGraphics';
import particleRegistry from './objects/particleGfx.json';

export interface ParticleAnim {
  sheet: string;
  frames: number[][];
  fps: number;
}

/** A bank-7 leaf/snow/dust emitter config (SceneCPUParticles params). */
export interface ParticleConfig {
  anim: ParticleAnim;
  tint: number;
  lifetime: number;
  direction: number;
  directionVar: number;
  velocity: number;
  velocityVar: number;
  gravity: number;
  gravityVar: number;
  gravityDir: number;
  drag: number;
  brownian: boolean;
  bx: number;
  bxSpeed: number;
  by: number;
  bySpeed: number;
  bexp: number;
  spawnInterval: number;
  generator: number | null;
  isGenerator: boolean;
}

const REG = particleRegistry as Record<string, ParticleConfig>;

export function getParticleConfig(x: number, y: number): ParticleConfig | null {
  return REG[`${x},${y}`] ?? null;
}

const MAX_PARTICLES = 400;

/** Uniform value in [value - variation, value + variation] (SceneCPUParticles.CalcVariation). */
function vary(value: number, variation: number): number {
  return value + (Math.random() * 2 * variation - variation);
}

// Smooth 1D value noise ≈ Godot OpenSimplexNoise.GetNoise1d (period ~64), range ~[-1,1].
function hash01(i: number, seed: number): number {
  let h = (i * 374761393 + seed * 668265263) | 0;
  h = (h ^ (h >>> 13)) * 1274126177;
  h = h ^ (h >>> 16);
  return ((h >>> 0) % 100000) / 100000;
}
function noise1d(x: number, seed: number): number {
  const t = x / 64;
  const xi = Math.floor(t);
  const xf = t - xi;
  const u = xf * xf * (3 - 2 * xf);
  const a = hash01(xi, seed);
  const b = hash01(xi + 1, seed);
  return (a + u * (b - a)) * 2 - 1;
}

class Particle {
  readonly sprite = new Sprite();
  active = false;
  private vx = 0;
  private vy = 0;
  private gx = 0;
  private gy = 0;
  private mass = 1;
  private drag = 0;
  private brownian = false;
  private bxForce = 0;
  private byForce = 0;
  private bxSpeed = 0;
  private bySpeed = 0;
  private bexp = 1.5;
  private btx = 0;
  private bty = 0;
  private seedX = 0;
  private seedY = 0;
  private life = 1;
  private age = 0;
  private params = 'default';
  private frames: Texture[] = [];
  private fps = 5;
  private animTime = 0;
  private animIndex = 0;
  private fading = false;
  private fadeT = 0;

  constructor() {
    this.sprite.anchor.set(0.5, 0.5);
    this.sprite.visible = false;
  }

  spawn(cfg: ParticleConfig, frames: Texture[], wx: number, wy: number): void {
    this.active = true;
    this.frames = frames;
    this.fps = cfg.anim.fps;
    this.animIndex = 0;
    this.animTime = 0;
    this.params = particleAnimKey(cfg);
    this.sprite.texture = frames[0] ?? Texture.WHITE;
    this.sprite.tint = cfg.tint;
    this.sprite.alpha = 1;
    this.sprite.visible = true;
    this.sprite.position.set(wx, wy);

    // Initial velocity + gravity vectors (magnitude · angle, each with variation).
    const dir = vary(cfg.direction, cfg.directionVar);
    const spd = vary(cfg.velocity, cfg.velocityVar);
    this.vx = Math.cos(dir) * spd;
    this.vy = Math.sin(dir) * spd;
    const gdir = cfg.gravityDir;
    const gmag = vary(cfg.gravity, cfg.gravityVar);
    this.gx = Math.cos(gdir) * gmag;
    this.gy = Math.sin(gdir) * gmag;

    this.mass = 1;
    this.drag = cfg.drag;
    this.brownian = cfg.brownian;
    this.bxForce = cfg.bx;
    this.byForce = cfg.by;
    this.bxSpeed = cfg.bxSpeed;
    this.bySpeed = cfg.bySpeed;
    this.bexp = cfg.bexp;
    this.btx = 0;
    this.bty = 999999; // yknytt seeds y-time offset so x/y noise differ
    this.seedX = (Math.random() * 1e9) | 0;
    this.seedY = (Math.random() * 1e9) | 0;
    this.life = vary(cfg.lifetime, 0);
    this.age = 0;
    this.fading = false;
    this.fadeT = 0;
  }

  update(dt: number, game: Game): void {
    if (!this.active) return;
    if (this.fading) {
      this.fadeT += dt;
      this.sprite.alpha = Math.max(0, 1 - this.fadeT / 0.3);
      if (this.fadeT >= 0.4) this.deactivate();
      return;
    }

    this.age += dt;
    if (this.age >= this.life) {
      this.deactivate();
      return;
    }

    // Brownian force (smooth noise raised to an exponent, keeping sign).
    let fx = 0;
    let fy = 0;
    if (this.brownian) {
      this.btx += this.bxSpeed * dt;
      this.bty += this.bySpeed * dt;
      let bmx = noise1d(this.btx, this.seedX) * this.bxForce;
      let bmy = noise1d(this.bty, this.seedY) * this.byForce;
      bmx = Math.pow(Math.abs(bmx), this.bexp) * Math.sign(bmx);
      bmy = Math.pow(Math.abs(bmy), this.bexp) * Math.sign(bmy);
      fx = bmx;
      fy = bmy;
    }

    // Euler integration (SceneCPUParticleInstance._PhysicsProcess).
    const ax = fx / this.mass + this.gx;
    const ay = fy / this.mass + this.gy;
    this.vx += (ax - this.drag * this.vx) * dt;
    this.vy += (ay - this.drag * this.vy) * dt;
    this.sprite.x += this.vx * dt;
    this.sprite.y += this.vy * dt;

    // Animate.
    if (this.frames.length > 1) {
      this.animTime += dt;
      const step = 1 / this.fps;
      while (this.animTime >= step) {
        this.animTime -= step;
        this.animIndex = (this.animIndex + 1) % this.frames.length;
        this.sprite.texture = this.frames[this.animIndex];
      }
    }

    // Collision: hit a solid tile → fade out (default/3/1) or recycle.
    if (game.collision.solidAt(this.sprite.x, this.sprite.y)) {
      if (this.params === 'default' || this.params === '3' || this.params === '1') {
        this.fading = true;
        this.fadeT = 0;
      } else {
        this.deactivate();
      }
    }
  }

  deactivate(): void {
    this.active = false;
    this.sprite.visible = false;
  }
}

/** The animation key a config maps to (mirrors the LeafParticle Params used for disappear). */
function particleAnimKey(cfg: ParticleConfig): string {
  if (cfg.anim.sheet.endsWith('snowflake.png')) return '3';
  if (cfg.anim.sheet.endsWith('dust.png')) return '14';
  if (cfg.anim.sheet.endsWith('leaf2.png')) return '1';
  if (cfg.anim.sheet.endsWith('firefall.png')) return '16'; // FallingStuff: recycle, no fade
  return 'default';
}

// Rain assets (Raindrop.tscn): rain.png = 3 drop types (8×8), splash.png = 8 frames (24×24).
const RAIN_SHEET = 'objects/bank7/img/rain.png';
const RAIN_FRAMES = [
  [0, 0, 8, 8],
  [0, 8, 8, 8],
  [0, 16, 8, 8],
];
const SPLASH_SHEET = 'objects/bank7/img/splash.png';
const SPLASH_FRAMES = [0, 1, 2, 3, 4, 5, 6, 7].map((i) => [i * 24, 0, 24, 24]);
const RAIN_BASE_FALL = 420;
const RAIN_VARIANCE = 60;
const SPLASH_FPS = 25;

/**
 * A falling raindrop (Raindrop.cs). Three speed types; falls straight down until it
 * travels `maxDistance`, then retires — or splashes (type-0 drops, 40% chance) when it
 * hits a solid tile.
 */
class RainDrop {
  readonly sprite = new Sprite();
  active = false;
  private speed = 0;
  private distance = 0;
  private maxDistance = 240;
  private type = 0;
  private splashing = false;
  private splashTime = 0;
  private dropFrames: Texture[] = [];
  private splashFrames: Texture[] = [];

  constructor() {
    this.sprite.anchor.set(0.5, 0.5);
    this.sprite.visible = false;
  }

  spawn(dropFrames: Texture[], splashFrames: Texture[], wx: number, wy: number, maxDistance: number): void {
    this.active = true;
    this.dropFrames = dropFrames;
    this.splashFrames = splashFrames;
    this.type = Math.floor(Math.random() * 3);
    this.speed = RAIN_BASE_FALL / (this.type + 1) - Math.random() * RAIN_VARIANCE;
    this.distance = 0;
    this.maxDistance = maxDistance;
    this.splashing = false;
    this.splashTime = 0;
    this.sprite.texture = dropFrames[this.type] ?? Texture.WHITE;
    this.sprite.alpha = 1;
    this.sprite.visible = true;
    this.sprite.position.set(wx, wy);
  }

  update(dt: number, game: Game): void {
    if (!this.active) return;
    if (this.splashing) {
      this.splashTime += dt;
      const idx = Math.floor(this.splashTime * SPLASH_FPS);
      if (idx >= this.splashFrames.length) {
        this.deactivate();
        return;
      }
      this.sprite.texture = this.splashFrames[idx];
      return;
    }
    const mv = this.speed * dt;
    this.sprite.y += mv;
    this.distance += mv;
    if (this.distance >= this.maxDistance) {
      this.deactivate();
      return;
    }
    if (game.collision.solidAt(this.sprite.x, this.sprite.y)) {
      if (this.type === 0 && Math.random() < 0.4) {
        this.splashing = true;
        this.splashTime = 0;
      } else {
        this.deactivate();
      }
    }
  }

  deactivate(): void {
    this.active = false;
    this.sprite.visible = false;
  }
}

// Cloud puff (CloudParticles.tscn CPUParticles2D): a small white circle that rises and
// fades over 0.6s, kicked up by Juni's feet/hands on Cloud (7,16) tiles.
const CIRCLE_SHEET = 'juni/circle.png';
const PUFF_LIFE = 0.6;

class CloudPuff {
  readonly sprite = new Sprite();
  active = false;
  private vx = 0;
  private vy = 0;
  private ax = 0;
  private ay = 0;
  private age = 0;
  private baseScale = 0.13;

  constructor() {
    this.sprite.anchor.set(0.5, 0.5);
    this.sprite.visible = false;
  }

  spawn(tex: Texture, wx: number, wy: number): void {
    this.active = true;
    this.age = 0;
    this.sprite.texture = tex;
    this.sprite.visible = true;
    // Emission sphere radius 3.
    const ed = Math.random() * Math.PI * 2;
    const er = Math.random() * 3;
    this.sprite.position.set(wx + Math.cos(ed) * er, wy + Math.sin(ed) * er);
    // Direction up (0,-1) with 20° spread; initial velocity 33.33 (±0.5 random).
    const ang = -Math.PI / 2 + (Math.random() - 0.5) * (20 * Math.PI) / 180;
    const spd = 33.33 * (1 - 0.5 * Math.random());
    this.vx = Math.cos(ang) * spd;
    this.vy = Math.sin(ang) * spd;
    // linear_accel 38.1 along velocity, tangential_accel -30.48 perpendicular.
    const la = 38.1 * (1 - 0.2 * Math.random());
    const ta = -30.48 * (1 - 0.55 * Math.random());
    this.ax = Math.cos(ang) * la + Math.cos(ang + Math.PI / 2) * ta;
    this.ay = Math.sin(ang) * la + Math.sin(ang + Math.PI / 2) * ta;
    this.baseScale = 0.13 * (1 - 0.22 * Math.random());
    this.sprite.scale.set(this.baseScale);
    this.sprite.alpha = 0.6;
  }

  update(dt: number): void {
    if (!this.active) return;
    this.age += dt;
    if (this.age >= PUFF_LIFE) {
      this.deactivate();
      return;
    }
    this.vx += this.ax * dt;
    this.vy += this.ay * dt;
    this.sprite.x += this.vx * dt;
    this.sprite.y += this.vy * dt;
    // color_ramp alpha 0.6 → 0 across lifetime.
    this.sprite.alpha = 0.6 * (1 - this.age / PUFF_LIFE);
  }

  deactivate(): void {
    this.active = false;
    this.sprite.visible = false;
  }
}

/** Pooled manager for all leaf/snow/dust particles + raindrops in the current area. */
export class ParticleSystem {
  readonly container = new Container();
  private particles: Particle[] = [];
  private raindrops: RainDrop[] = [];
  private frameCache = new Map<string, Texture[] | null>();
  private rainFrames: Texture[] | null = null;
  private splashFrames: Texture[] | null = null;
  private rainLoading = false;
  private puffs: CloudPuff[] = [];
  private circleTex: Texture | null = null;
  private circleLoading = false;

  constructor(private game: Game) {
    this.container.zIndex = 12; // foreground ambiance (object layer 3 level)
  }

  /** Preload rain + splash frames (async, once). */
  preloadRain(): void {
    if (this.rainLoading || this.rainFrames) return;
    this.rainLoading = true;
    void loadFrames(this.game, RAIN_SHEET, RAIN_FRAMES).then((f) => (this.rainFrames = f));
    void loadFrames(this.game, SPLASH_SHEET, SPLASH_FRAMES).then((f) => (this.splashFrames = f));
  }

  /** Emit a raindrop at a world position, falling up to maxDistance px. */
  spawnRain(wx: number, wy: number, maxDistance: number): void {
    if (!this.rainFrames || !this.splashFrames) {
      this.preloadRain();
      return;
    }
    let d = this.raindrops.find((q) => !q.active);
    if (!d) {
      if (this.raindrops.length >= MAX_PARTICLES) d = this.raindrops[0];
      else {
        d = new RainDrop();
        this.raindrops.push(d);
        this.container.addChild(d.sprite);
      }
    }
    d.spawn(this.rainFrames, this.splashFrames, wx, wy, maxDistance);
  }

  /** Preload a config's frames (async, cached by sheet). */
  preload(cfg: ParticleConfig): void {
    if (this.frameCache.has(cfg.anim.sheet)) return;
    this.frameCache.set(cfg.anim.sheet, null);
    void loadFrames(this.game, cfg.anim.sheet, cfg.anim.frames).then((f) =>
      this.frameCache.set(cfg.anim.sheet, f),
    );
  }

  /** Emit one particle for the config at a world position. */
  spawn(cfg: ParticleConfig, wx: number, wy: number): void {
    const frames = this.frameCache.get(cfg.anim.sheet);
    if (!frames) {
      this.preload(cfg);
      return;
    }
    let p = this.particles.find((q) => !q.active);
    if (!p) {
      if (this.particles.length >= MAX_PARTICLES) p = this.particles[0];
      else {
        p = new Particle();
        this.particles.push(p);
        this.container.addChild(p.sprite);
      }
    }
    p.spawn(cfg, frames, wx, wy);
  }

  /** Preload Juni's cloud-puff texture (circle.png), once. */
  preloadPuff(): void {
    if (this.circleLoading || this.circleTex) return;
    this.circleLoading = true;
    void this.game.assets.getTexture(CIRCLE_SHEET).then((t) => (this.circleTex = t ?? null));
  }

  /** Emit a cloud puff at a world position (Juni's foot/hand on a Cloud tile). */
  spawnCloudPuff(wx: number, wy: number): void {
    if (!this.circleTex) {
      this.preloadPuff();
      return;
    }
    let p = this.puffs.find((q) => !q.active);
    if (!p) {
      if (this.puffs.length >= MAX_PARTICLES) p = this.puffs[0];
      else {
        p = new CloudPuff();
        this.puffs.push(p);
        this.container.addChild(p.sprite);
      }
    }
    p.spawn(this.circleTex, wx, wy);
  }

  update(dt: number): void {
    for (const p of this.particles) p.update(dt, this.game);
    for (const d of this.raindrops) d.update(dt, this.game);
    for (const p of this.puffs) p.update(dt);
  }

  clear(): void {
    for (const p of this.particles) p.deactivate();
    for (const d of this.raindrops) d.deactivate();
    for (const p of this.puffs) p.deactivate();
  }

  destroy(): void {
    this.clear();
    this.container.destroy({ children: true });
  }
}
